import { __decorate, __metadata, __param } from "tslib";
import { Symbols } from '@/symbols';
import { inject, injectable } from 'inversify';
import _, { concat, forEach, mapKeys, camelCase, isEmpty, flatMap } from 'lodash';
import { E_Package_ChargeMode } from '@/apis/service/PackageApi.types';
let PackagesService = class PackagesService {
    constructor(miscApi, i18n, packageApi, productApi, companyOrganizationCommonService, productService) {
        this.miscApi = miscApi;
        this.i18n = i18n;
        this.packageApi = packageApi;
        this.productApi = productApi;
        this.companyOrganizationCommonService = companyOrganizationCommonService;
        this.productService = productService;
        // 系统code数据相关
        this.codesTransformSelectedArray = (sysCodeData) => {
            if (!sysCodeData)
                return [];
            return sysCodeData.reduce((total, cur) => {
                total.push({
                    value: cur.system_code,
                    en: _.get(cur, ['system_code_text', 'en_US']),
                    cn: _.get(cur, ['system_code_text', 'zh_CN']),
                });
                return total;
            }, []);
        };
        this.getSubProducts = (product) => {
            return this.miscApi.getSystemCodes(product);
        };
        this.allProducts = [];
        this.productLocalMapData = {};
        // 产品相关
        this.packageProducts = [];
    }
    // 资源服务器
    async getClientResourceServers() {
        const data = await this.miscApi.getClientResourceServers() || [];
        return this.codesTransformSelectedArray(data);
    }
    // 客户端类别
    async getClientType() {
        const data = await this.miscApi.getClientType() || [];
        return this.codesTransformSelectedArray(data);
    }
    // 客户端权限范围
    async getClientScopes() {
        const data = await this.miscApi.getClientScopes() || [];
        return this.codesTransformSelectedArray(data);
    }
    // 授权类别
    async getClientAllowedGrantTypes() {
        const data = await this.miscApi.getClientAllowedGrantTypes() || [];
        return this.codesTransformSelectedArray(data);
    }
    async getAllProducts() {
        let products = this.allProducts;
        if (!products?.length) {
            products = await this.productService.getProductionProducts();
            this.allProducts = products;
        }
        return products;
    }
    // 获取产品- 客户端列表
    async getClientsList(params) {
        return await this.productApi.getClientsList(params).then((data) => data.aaData);
    }
    // 客户端详情
    async getClientDetail(id) {
        return await this.productApi.getClientDetail(id);
    }
    // 新增修改 客户端
    async postClients(params) {
        return await this.productApi.postClients(params);
    }
    //  修改客户端
    async patchClients(id, params) {
        return await this.productApi.patchClients(id, params);
    }
    async deleteClients(id) {
        const clients = await this.productApi.deleteClients(id);
        return clients;
    }
    // 客户端权限
    async getClientRole(id) {
        return await this.productApi.getClientRole(id);
    }
    // clientId:string, productRoleId
    async postClientRole(params) {
        return await this.productApi.postClientRole(params);
    }
    async deleteClientRole(clientId, productRoleId) {
        return await this.productApi.deleteClientRole(clientId, productRoleId);
    }
    async getAllRoles() {
        return await this.productApi.getAllRoles();
    }
    get packageProductsOnSale() {
        return _.filter(this.packageProducts, item => item.on_sale);
    }
    async updatePackageProducts() {
        this.packageProducts = await this.getProducts();
    }
    getPackageProductById(productId) {
        return this.packageProducts.find(it => it.product_id === productId);
    }
    getPackageProductLabel(options) {
        let item = options;
        if (typeof options === 'string') {
            item = this.getPackageProductById(options);
        }
        return _.get(item, `alias_${this.i18n.locale}`, '') || _.get(item, 'product_name', '');
    }
    async getProducts() {
        const products = await this.productService.getProductionProducts();
        return products;
    }
    async getResProducts() {
        const products = await this.productService.getAllProductionProducts();
        return products;
    }
    async postProducts(params) {
        const products = await this.productApi.postProducts(params);
        return products;
    }
    async deleteProducts(id) {
        const products = await this.productApi.deleteProducts(id);
        return products;
    }
    async getWithUnlockedRoles(productIds) {
        const roleList = await this.productApi.getWithUnlockedRoles(productIds);
        let roles = [];
        forEach(roleList, item => {
            roles = concat(roles, item.product_role_list);
        });
        return roles;
    }
    async getWithUnlockedRolesMap(productIds) {
        const roleList = await this.productApi.getWithUnlockedRoles(productIds);
        return roleList;
    }
    async getFeatureCapacityRoles(productIds) {
        const roleList = await this.productApi.getFeatureCapacityRoles(productIds);
        return roleList;
    }
    async loadPackages(queriesData) {
        const packageList = await this.packageApi.getListPackages(queriesData);
        return packageList;
    }
    async postCreatePackage(queriesData) {
        return this.packageApi.postCreatePackage(queriesData);
    }
    async patchUpdatePackage(queriesData) {
        return this.packageApi.patchUpdatePackage(queriesData);
    }
    async getPackage(packageId) {
        const res = await this.packageApi.getPackage(packageId);
        // 蛇形转驼峰
        const data = mapKeys(res, (value, key) => {
            return camelCase(key);
        });
        return data;
    }
    async getPackageMap(packageId) {
        const res = await this.packageApi.getPackage(packageId);
        // 蛇形转驼峰
        return res;
    }
    async getProductLocalMap() {
        if (!isEmpty(this.productLocalMapData)) {
            return this.productLocalMapData;
        }
        const products = await this.getAllProducts();
        const productLocalMap = {};
        forEach(products, item => {
            productLocalMap[item.product_id] = {
                alias_cn: item.alias_cn,
                alias_en: item.alias_en,
            };
        });
        this.productLocalMapData = productLocalMap;
        return productLocalMap;
    }
    async getOptionsChargeModes() {
        const res = await this.companyOrganizationCommonService.getChargeModes();
        const charge_modes = _.filter(res, item => _.includes(E_Package_ChargeMode, item));
        return charge_modes;
    }
    getProductIdsFromPackages(packageList) {
        const productIds = flatMap(packageList, item => {
            if (item && item.product_ids) {
                return [...item.product_ids];
            }
            return [];
        });
        return productIds;
    }
    getPackages() {
        return this.packageApi.getPackageInfoList();
    }
    getEnabledPackages() {
        return this.packageApi.getPackageInfoList().then(res => {
            return _.filter(res, item => item.enabled);
        });
    }
    transformPackageToRoleListFilterProduct(roleListMap, packageList) {
        const productListFromPackage = this.getProductIdsFromPackages(packageList);
        const _roleListMap = _.filter(roleListMap, item => {
            return _.includes(productListFromPackage, item.product_id);
        });
        return this.transformPackageToRoleList(_roleListMap, packageList);
    }
    transformPackageToRoleList(roleListMap, packageList) {
        roleListMap = _.cloneDeep(roleListMap);
        packageList = _.cloneDeep(packageList);
        const data = [];
        const mergeCells = [];
        const mergeCellItem = {
            row: 0,
            col: 0,
            rowspan: 0,
            colspan: 1,
        };
        _.forEach(roleListMap, item => {
            const _mergeCellItem = { ...(_.last(mergeCells) || mergeCellItem) };
            _mergeCellItem.row = _mergeCellItem.rowspan + _mergeCellItem.row;
            _mergeCellItem.rowspan = _.get(item, 'product_role_list.length', 0);
            mergeCells.push(_mergeCellItem);
            _.forEach(item.product_role_list, roleItem => {
                roleItem = _.merge(roleItem, {
                    alias_cn: item.alias_cn,
                    alias_en: item.alias_en,
                    product_name: item.product_name,
                });
                if (packageList && packageList.length) {
                    _.forEach(packageList, pkgItem => {
                        if (pkgItem) {
                            const isFeatureCheck = _.includes(pkgItem.product_role_ids, roleItem.id);
                            const capacity_resources = _.flatMap(roleItem.capacity_resources, (capacityRoleItem) => {
                                const capacity_options = _.filter(capacityRoleItem.capacity_options, subItem => {
                                    return subItem && _.includes(pkgItem.product_role_ids, subItem.id);
                                });
                                if (capacity_options.length) {
                                    return [{
                                            ...capacityRoleItem,
                                            capacity_options,
                                        }];
                                }
                                return [];
                            });
                            roleItem[pkgItem.package_id] = {
                                isFeatureCheck,
                                capacity_resources: capacity_resources,
                            };
                        }
                    });
                }
                data.push(roleItem);
            });
        });
        return { data, mergeCells };
    }
};
PackagesService = __decorate([
    injectable(),
    __param(0, inject(Symbols.MiscApi)),
    __param(1, inject(Symbols.I18n)),
    __param(2, inject(Symbols.PackageApi)),
    __param(3, inject(Symbols.ProductApi)),
    __param(4, inject(Symbols.CompanyOrganizationCommonService)),
    __param(5, inject(Symbols.ProductService)),
    __metadata("design:paramtypes", [Object, Object, Object, Object, Object, Object])
], PackagesService);
export { PackagesService };
